import { Capacitor } from '@capacitor/core';
import { PushNotifications, type ActionPerformed } from '@capacitor/push-notifications';
import { router } from '../App';

/**
 * Native-only "tap a push notification → open the right screen" (Android/iOS
 * via Capacitor). This is the exact native counterpart of the web build's
 * service worker: public/firebase-messaging-sw.js reads `payload.data.path`
 * off the SAME backend-sent FCM message and calls `client.navigate(path)` on
 * `notificationclick`. Every producer (FriendsController#accept/#create,
 * GameInvitationsController#create, TurnNotifier, EvaluateAchievementsJob,
 * DirectMessagesController#create) already attaches a `path` string to the
 * `data` payload for exactly this purpose — see
 * PushNotifications::SenderService on the backend, which sends `data` as a
 * flat string map alongside the `notification` block FCM needs to show a
 * system-tray notification while the app is backgrounded/killed.
 *
 * Deliberately narrow scope (Phase 11 respected): this module ONLY reacts to
 * an explicit notification TAP (`pushNotificationActionPerformed`). It never
 * touches registration, permissions, or push_enabled — none of the identity-
 * guarded machinery in utils/push.ts / pushNative.ts is used or duplicated
 * here. It also does not add a `pushNotificationReceived` (foreground
 * arrival) handler — pushWeb.ts's `onMessage(messaging, () => {})` is an
 * intentional no-op for the same case (the app already reflects these events
 * live via ActionCable while foregrounded), so native mirrors that silence
 * rather than inventing new foreground behavior.
 *
 * Registered once, from main.tsx, BEFORE the first React render (like
 * initNativeChrome) — Capacitor buffers the launch notification (the one
 * that cold-started the app) and delivers it to the first listener that
 * attaches, so registering this early also covers "tap while the app was
 * fully killed", not just background/foreground taps.
 */
export function initNativePushDeepLinking(): void {
  if (!Capacitor.isNativePlatform()) return;

  void PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
    const path = action.notification?.data?.path;
    if (typeof path !== 'string' || !path.startsWith('/')) return; // no path, or not an in-app route — nothing to do
    router.navigate(path);
  });
}
